import distance from "@turf/distance";
import { point } from "@turf/helpers";

export type DistanceUnit = "kilometers" | "miles" | "meters";
export type Linkage = "single" | "complete" | "average";

export interface InputPoint {
  id: string;
  latitude: number;
  longitude: number;
  properties: Record<string, unknown>;
}

export interface ClusteredPoint extends InputPoint {
  clusterId: number;
  clusterSize: number;
}

interface HeapEntry {
  left: number;
  right: number;
  distance: number;
}

class MinHeap {
  private readonly values: HeapEntry[] = [];

  push(value: HeapEntry): void {
    this.values.push(value);
    let index = this.values.length - 1;
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.values[parent].distance <= value.distance) break;
      this.values[index] = this.values[parent];
      index = parent;
    }
    this.values[index] = value;
  }

  pop(): HeapEntry | undefined {
    const first = this.values[0];
    const last = this.values.pop();
    if (!first || !last || this.values.length === 0) return first;
    let index = 0;
    while (true) {
      const left = index * 2 + 1;
      const right = left + 1;
      if (left >= this.values.length) break;
      const child = right < this.values.length && this.values[right].distance < this.values[left].distance ? right : left;
      if (this.values[child].distance >= last.distance) break;
      this.values[index] = this.values[child];
      index = child;
    }
    this.values[index] = last;
    return first;
  }
}

class UnionFind {
  private readonly parent: Int32Array;
  private readonly rank: Uint8Array;

  constructor(size: number) {
    this.parent = Int32Array.from({ length: size }, (_, index) => index);
    this.rank = new Uint8Array(size);
  }

  find(value: number): number {
    let root = value;
    while (this.parent[root] !== root) root = this.parent[root];
    while (this.parent[value] !== value) {
      const next = this.parent[value];
      this.parent[value] = root;
      value = next;
    }
    return root;
  }

  union(left: number, right: number): void {
    let rootLeft = this.find(left);
    let rootRight = this.find(right);
    if (rootLeft === rootRight) return;
    if (this.rank[rootLeft] < this.rank[rootRight]) [rootLeft, rootRight] = [rootRight, rootLeft];
    this.parent[rootRight] = rootLeft;
    if (this.rank[rootLeft] === this.rank[rootRight]) this.rank[rootLeft]++;
  }
}

function toKilometers(value: number, unit: DistanceUnit): number {
  if (unit === "meters") return value / 1000;
  if (unit === "miles") return value * 1.609344;
  return value;
}

function haversine(left: InputPoint, right: InputPoint): number {
  return distance(point([left.longitude, left.latitude]), point([right.longitude, right.latitude]), { units: "kilometers" });
}

function assign(points: InputPoint[], groups: number[][]): ClusteredPoint[] {
  const ordered = groups.sort((a, b) => b.length - a.length || a[0] - b[0]);
  const assignments = new Map<number, { id: number; size: number }>();
  ordered.forEach((indices, clusterIndex) => {
    indices.forEach((index) => assignments.set(index, { id: clusterIndex + 1, size: indices.length }));
  });
  return points.map((input, index) => ({ ...input, clusterId: assignments.get(index)!.id, clusterSize: assignments.get(index)!.size }));
}

function singleLink(points: InputPoint[], thresholdKm: number): ClusteredPoint[] {
  const latCell = thresholdKm / 110.574;
  const lonCell = thresholdKm / 111.32;
  const unionFind = new UnionFind(points.length);
  const grid = new Map<string, number[]>();

  points.forEach((current, index) => {
    const cellY = Math.floor(current.latitude / latCell);
    const cellX = Math.floor(current.longitude / lonCell);
    const longitudeCells = Math.min(Math.ceil(1 / Math.max(Math.cos((current.latitude * Math.PI) / 180), 0.01)) + 1, 102);

    for (let y = cellY - 1; y <= cellY + 1; y++) {
      for (let x = cellX - longitudeCells; x <= cellX + longitudeCells; x++) {
        const candidates = grid.get(`${y}:${x}`);
        if (!candidates) continue;
        for (const candidateIndex of candidates) {
          if (haversine(current, points[candidateIndex]) <= thresholdKm) unionFind.union(index, candidateIndex);
        }
      }
    }

    const key = `${cellY}:${cellX}`;
    const bucket = grid.get(key);
    if (bucket) bucket.push(index);
    else grid.set(key, [index]);
  });

  const members = new Map<number, number[]>();
  points.forEach((_, index) => {
    const root = unionFind.find(index);
    const bucket = members.get(root);
    if (bucket) bucket.push(index);
    else members.set(root, [index]);
  });
  return assign(points, [...members.values()]);
}

function pairKey(left: number, right: number): string {
  return left < right ? `${left}:${right}` : `${right}:${left}`;
}

function hierarchicalLink(points: InputPoint[], thresholdKm: number, linkage: Exclude<Linkage, "single">): ClusteredPoint[] {
  if (points.length > 1200) throw new Error(`${linkage === "complete" ? "Complete" : "Average"} linkage is limited to 1,200 points in this browser release.`);

  const active = new Set<number>();
  const members = new Map<number, number[]>();
  const sizes = new Map<number, number>();
  const distances = new Map<string, number>();
  const heap = new MinHeap();

  points.forEach((_, index) => {
    active.add(index);
    members.set(index, [index]);
    sizes.set(index, 1);
  });

  for (let left = 0; left < points.length; left++) {
    for (let right = left + 1; right < points.length; right++) {
      const measured = haversine(points[left], points[right]);
      distances.set(pairKey(left, right), measured);
      heap.push({ left, right, distance: measured });
    }
  }

  let nextId = points.length;
  while (true) {
    let candidate: HeapEntry | undefined;
    while ((candidate = heap.pop())) {
      if (!active.has(candidate.left) || !active.has(candidate.right)) continue;
      const current = distances.get(pairKey(candidate.left, candidate.right));
      if (current !== undefined && Math.abs(current - candidate.distance) < 1e-12) break;
    }
    if (!candidate || candidate.distance > thresholdKm) break;

    const mergedId = nextId++;
    const leftSize = sizes.get(candidate.left)!;
    const rightSize = sizes.get(candidate.right)!;
    const mergedMembers = [...members.get(candidate.left)!, ...members.get(candidate.right)!];
    const others = [...active].filter((id) => id !== candidate!.left && id !== candidate!.right);

    active.delete(candidate.left);
    active.delete(candidate.right);
    active.add(mergedId);
    members.set(mergedId, mergedMembers);
    sizes.set(mergedId, leftSize + rightSize);

    for (const other of others) {
      const leftDistance = distances.get(pairKey(candidate.left, other))!;
      const rightDistance = distances.get(pairKey(candidate.right, other))!;
      const mergedDistance = linkage === "complete"
        ? Math.max(leftDistance, rightDistance)
        : (leftDistance * leftSize + rightDistance * rightSize) / (leftSize + rightSize);
      distances.set(pairKey(mergedId, other), mergedDistance);
      heap.push({ left: mergedId, right: other, distance: mergedDistance });
    }
  }

  return assign(points, [...active].map((id) => members.get(id)!));
}

export function clusterPoints(
  points: InputPoint[],
  threshold: number,
  unit: DistanceUnit,
  linkage: Linkage = "single",
): ClusteredPoint[] {
  if (!Number.isFinite(threshold) || threshold <= 0) throw new Error("Distance threshold must be greater than zero.");
  if (points.length === 0) return [];
  const thresholdKm = toKilometers(threshold, unit);
  return linkage === "single" ? singleLink(points, thresholdKm) : hierarchicalLink(points, thresholdKm, linkage);
}